import { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare, X, Check, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { LearningFeedback, SubmitFeedbackInput } from '@/features/review/hooks/useFeedback';

// =========================================
// FeedbackButton — inline feedback on a criterion score
// =========================================

interface FeedbackButtonProps {
  evaluationId: string;
  criteriaId: string;
  originalScore: number;
  existingFeedback?: LearningFeedback;
  onSubmit: (input: SubmitFeedbackInput) => void;
  onDelete?: (feedbackId: string) => void;
  isSubmitting?: boolean;
}

export function FeedbackButton({
  evaluationId,
  criteriaId,
  originalScore,
  existingFeedback,
  onSubmit,
  onDelete,
  isSubmitting,
}: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const [correctedScore, setCorrectedScore] = useState(
    existingFeedback?.corrected_score ?? originalScore
  );
  const [note, setNote] = useState(existingFeedback?.feedback_note ?? '');

  const hasFeedback = !!existingFeedback;
  const isAgreement = hasFeedback && existingFeedback.corrected_score === existingFeedback.original_score;
  const isDisagreement = hasFeedback && !isAgreement;

  function handleQuickAgree() {
    onSubmit({
      evaluation_id: evaluationId,
      criteria_id: criteriaId,
      original_score: originalScore,
      corrected_score: originalScore,
      feedback_note: 'Agreed with AI score',
    });
  }

  function handleQuickDisagree() {
    setOpen(true);
  }

  function handleSubmitCorrection() {
    onSubmit({
      evaluation_id: evaluationId,
      criteria_id: criteriaId,
      original_score: originalScore,
      corrected_score: correctedScore,
      feedback_note: note || undefined,
    });
    setOpen(false);
  }

  function handleUndo() {
    if (existingFeedback && onDelete) {
      onDelete(existingFeedback.id);
    }
  }

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {hasFeedback ? (
        <div className="flex items-center gap-1.5">
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] px-1.5 gap-1',
              isAgreement
                ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                : 'border-amber-300 text-amber-700 bg-amber-50'
            )}
          >
            {isAgreement ? (
              <ThumbsUp className="h-3 w-3" />
            ) : (
              <MessageSquare className="h-3 w-3" />
            )}
            {isDisagreement
              ? `Corrected: ${existingFeedback.corrected_score}%`
              : 'Confirmed'}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleUndo}
            title="Remove feedback"
          >
            <Undo2 className="h-3 w-3 text-slate-400" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
            onClick={handleQuickAgree}
            disabled={isSubmitting}
            title="Agree with score"
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </Button>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                onClick={handleQuickDisagree}
                disabled={isSubmitting}
                title="Correct this score"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end" side="top">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-900">
                    Correct Score
                  </h4>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500">
                      AI score: {Math.round(originalScore)}%
                    </Label>
                    <span className="text-sm font-semibold text-indigo-600">
                      {Math.round(correctedScore)}%
                    </span>
                  </div>
                  <Slider
                    value={[correctedScore]}
                    onValueChange={(values) => setCorrectedScore(values[0] ?? 0)}
                    min={0}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Note (optional)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Why should this score be different?"
                    className="h-16 text-xs resize-none"
                  />
                </div>

                <Button
                  size="sm"
                  className="w-full"
                  onClick={handleSubmitCorrection}
                  disabled={isSubmitting}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Save Correction
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
